import { useEffect, useRef } from 'react';
import { useAccountInfo } from '../api/queryHooks/account';

const setIconLink = (rel: 'icon' | 'apple-touch-icon', href: string) => {
  const selector = rel === 'icon' ? "link[rel~='icon']" : "link[rel='apple-touch-icon']";
  const existing = document.querySelector<HTMLLinkElement>(selector);
  if (existing) {
    existing.href = href;
    return;
  }
  const link = document.createElement('link');
  link.rel = rel;
  link.href = href;
  document.head.appendChild(link);
};

/**
 * Single owner of the document head bits that belong to the shop: the title
 * and the two icon links. Called once from ThemeProvider, which wraps the
 * whole router (admin routes included) — the title used to live in MainLayout
 * and the favicon in ThemeProvider, so admin pages never got a shop title.
 *
 * The server resolves the fallback (Setting#web_title, else Account#name), so
 * display_name is used as-is; index.html's "Foto2Me" stands until it arrives.
 */
export function useShopHead(): void {
  const { data } = useAccountInfo();
  const displayName = data?.display_name ?? null;
  const faviconUrl = (data?.setting?.favicon_url as string | undefined) ?? null;

  // Captured before we ever change them, so removing the shop favicon can put
  // the platform icons back without a reload.
  const originalIcons = useRef<{ icon: string | null; apple: string | null } | undefined>(undefined);

  useEffect(() => {
    if (displayName) document.title = displayName;
  }, [displayName]);

  useEffect(() => {
    if (originalIcons.current === undefined) {
      originalIcons.current = {
        icon: document.querySelector<HTMLLinkElement>("link[rel~='icon']")?.href ?? null,
        apple: document.querySelector<HTMLLinkElement>("link[rel='apple-touch-icon']")?.href ?? null,
      };
    }

    if (faviconUrl) {
      setIconLink('icon', faviconUrl);
      setIconLink('apple-touch-icon', faviconUrl);
      return;
    }

    const original = originalIcons.current;
    if (original.icon) setIconLink('icon', original.icon);
    if (original.apple) setIconLink('apple-touch-icon', original.apple);
  }, [faviconUrl]);
}
